import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, MapPin, Edit, Trash2, ArrowLeft, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LocationList = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editLocation, setEditLocation] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredLocations(locations.filter(l => 
        l.name?.toLowerCase().includes(term) ||
        l.address?.toLowerCase().includes(term) ||
        l.customer_name?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredLocations(locations);
    }
  }, [locations, searchTerm]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Sind Sie sicher? Alle Daten dieses Standorts werden gelöscht!')) return;
    
    try {
      await axios.delete(`${API}/superadmin/locations/${locationId}`);
      toast.success('Standort gelöscht');
      fetchLocations();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleEdit = (location) => {
    setEditLocation({ ...location });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/superadmin/locations/${editLocation.id}`, editLocation);
      toast.success('Standort aktualisiert');
      setEditDialogOpen(false);
      fetchLocations();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Standortverwaltung
            </h1>
            <p className="text-muted-foreground mt-1">Alle Standorte im System</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Suchen..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filteredLocations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Keine Standorte gefunden</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredLocations.map((location) => (
              <Card key={location.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <MapPin className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{location.name}</h3>
                        {location.address && (
                          <p className="text-sm text-muted-foreground">{location.address}</p>
                        )}
                        {location.customer_name && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                            <Building2 className="h-3 w-3" />
                            <span>{location.customer_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(location)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(location.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Location Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Standort bearbeiten</DialogTitle>
          </DialogHeader>
          {editLocation && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={editLocation.name || ''} 
                  onChange={(e) => setEditLocation({...editLocation, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input 
                  value={editLocation.address || ''} 
                  onChange={(e) => setEditLocation({...editLocation, address: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveEdit}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
